/**
 * The enrol sheet's words to the enrolment and capture calls, and back (slices 04–05). Names
 * are optional: blank fields go out as null, and an unnamed subject is labelled by the
 * enrolment time and its short id ("Neznámy · 14:05 · 7f3a").
 */
import { clamp01 } from '@/components/faces/highlight'
import type { EnrolFlowView, EnrolSaveView } from '@/components/humint/check/EnrolPanelStatus'
import type { EncounterGap } from '@/components/humint/encounter/encounter-draft'
import type { EncounterLiveView } from '@/components/humint/encounter/encounter-types'
import type { NormalizedBox } from '@/components/humint/types'
import type {
  EncounterJoinDto,
  EnrolOriginDto,
  EnrolSubjectRequest,
  EnrolSubjectResponse,
  FaceBoxDto,
} from '@/services/api/humint'
import type { EncounterLinkRequest } from '@/services/api/humint-encounters'
import { opensEncounter } from '../encounter/enrol-encounter'
import {
  canRetry,
  type EnrolAttempt,
  type EnrolledSubject,
  type EnrolState,
  enrolledSubject,
  isPhotoUnsaved,
} from '../hooks/useEnrolSubject.machine'
import { needsRetake } from './enrol-errors'

type RandomSource = Pick<Crypto, 'getRandomValues'> & { randomUUID?: () => string }

/**
 * A v4 UUID. `crypto.randomUUID` exists only in secure contexts; a phone on a plain-http test
 * deployment falls back to `getRandomValues`, which is available everywhere.
 */
export function randomUuid(source: RandomSource = globalThis.crypto): string {
  if (typeof source.randomUUID === 'function') return source.randomUUID()
  const bytes = source.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** A fresh attempt: its ids minted once, reused by every retry of this attempt. */
export function newEnrolAttempt(
  withPhoto: boolean,
  now: Date = new Date(),
  source?: RandomSource
): EnrolAttempt {
  return {
    ids: {
      enrolmentId: randomUuid(source),
      captureId: randomUuid(source),
      encounterId: randomUuid(source),
    },
    startedAt: now.toISOString(),
    withPhoto,
  }
}

export interface EnrolNames {
  givenName: string
  surname: string
  nickname: string
}

/** No names typed; also what *Add photo* sends (its subject exists). */
export const EMPTY_NAMES: EnrolNames = { givenName: '', surname: '', nickname: '' }

const orNull = (value: string) => value.trim() || null

export interface EnrolRequestContext {
  enrolmentId: string
  origin: EnrolOriginDto
  checkId: string | null
  /**
   * The encounter the subject is enrolled from (slice 05), or one that exists joined with the
   * subject's own values (slice 06); null records none.
   */
  encounter: EncounterLinkRequest | null
}

/** A new encounter goes as `encounter`; a join's fields go beside the names. */
function encounterFields(
  part: EncounterLinkRequest | null
): Pick<EnrolSubjectRequest, 'encounter'> & Partial<EncounterJoinDto> {
  if (part === null) return { encounter: null }
  return opensEncounter(part) ? { encounter: part.encounter } : { encounter: null, ...part }
}

export function toEnrolRequest(
  names: EnrolNames,
  context: EnrolRequestContext
): EnrolSubjectRequest {
  return {
    enrolment_id: context.enrolmentId,
    first_name: orNull(names.givenName),
    last_name: orNull(names.surname),
    nickname: orNull(names.nickname),
    origin: context.origin,
    check_id: context.checkId,
    ...encounterFields(context.encounter),
  }
}

export function toEnrolledSubject(dto: EnrolSubjectResponse): EnrolledSubject {
  return {
    subjectRef: dto.subject_ref,
    shortId: dto.short_id,
    displayName: dto.display_name,
    enrolledAt: dto.enrolled_at,
    encounterRef: dto.encounter_ref ?? null,
  }
}

/**
 * The face box the check used, back in the API's `[x1, y1, x2, y2]` form. A detector's box may
 * overhang the photo; the server takes only `0 <= x1 < x2 <= 1` (and so for y), so it is clipped
 * to the photo, and none is sent when nothing of it is left.
 */
export function toProbeBox(box: NormalizedBox | null | undefined): FaceBoxDto | null {
  if (!box) return null
  const [x1, y1, x2, y2] = [box.x, box.y, box.x + box.width, box.y + box.height].map(clamp01)
  return x1 < x2 && y1 < y2 ? [x1, y1, x2, y2] : null
}

/** The machine's state as the enrol sheet shows it. */
export function toEnrolSaveView(state: EnrolState): EnrolSaveView {
  switch (state.step) {
    case 'editing':
      return { step: 'editing', problem: state.problem ?? null }
    case 'enrolling':
      return { step: 'enrolling' }
    case 'uploading':
      return { step: 'uploading', sentBytes: state.sentBytes, totalBytes: state.totalBytes }
    case 'saved':
      return { step: 'saved', capture: state.capture }
    case 'failed':
      return {
        step: 'failed',
        problem: state.problem,
        canRetry: canRetry(state),
        retake: needsRetake(state.problem),
      }
  }
}

/** What the live sheet shows beside the machine: the case, the GPS and tag lookup, the gaps. */
export interface EnrolFlowFacts {
  caseName: string
  live: EncounterLiveView
  gaps: readonly EncounterGap[]
}

/** Everything the live enrol sheet needs from the machine and the encounter's live facts. */
export function toEnrolFlowView(state: EnrolState, facts: EnrolFlowFacts): EnrolFlowView {
  const subject = enrolledSubject(state)
  return {
    save: toEnrolSaveView(state),
    label: {
      at: subject?.enrolledAt ?? state.attempt.startedAt,
      shortId: subject?.shortId ?? null,
    },
    subjectCreated: subject !== null,
    photoUnsaved: isPhotoUnsaved(state),
    ...facts,
  }
}
